import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URL } from '../utils/api';

const MonthlyRecap = () => {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedClassroom, setSelectedClassroom] = useState('all');
  const [personTab, setPersonTab] = useState('student');
  const isTeacherTab = personTab === 'teacher';

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportParams, setExportParams] = useState({
    month: selectedMonth,
    year: selectedYear,
    grade: selectedGrade,
    classroom: selectedClassroom,
  });

  const months = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const grades = [1, 2, 3, 4, 5, 6];
  const classrooms = ['A', 'B', 'C'];

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear, selectedGrade, selectedClassroom, personTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/reports/monthly-recap`, {
        params: {
          month: selectedMonth,
          year: selectedYear,
          grade: isTeacherTab ? 'all' : selectedGrade,
          classroom: isTeacherTab ? 'all' : selectedClassroom,
          person_type: personTab,
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching monthly recap:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const isSunday = (day) => new Date(selectedYear, selectedMonth - 1, day).getDay() === 0;

  const sundayHeaderSx = {
    bgcolor: '#7c3aed !important',
    color: 'white',
    border: '1px solid #5b21b6',
  };

  const sundayBodySx = {
    bgcolor: '#ede9fe',
    color: '#5b21b6',
    fontWeight: 600,
  };

  const getStatusCode = (status) => {
    switch (status) {
      case 'present': return 'H';
      case 'late': return 'T';
      case 'permission': return 'I';
      case 'sick': return 'S';
      case 'absent': return 'A';
      default: return '-';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return '#d1fae5'; // green-100
      case 'late': return '#fef3c7'; // yellow-100
      case 'permission': return '#dbeafe'; // blue-100
      case 'sick': return '#e0e7ff'; // indigo-100
      case 'absent': return '#fee2e2'; // red-100
      default: return 'transparent';
    }
  };

  const getEffectiveCheckInStatus = (student, day) => {
    const attendance = student.attendance[day];
    if (attendance?.checkInStatus) return attendance.checkInStatus;

    // Check if date is valid for Alpha
    const date = new Date(selectedYear, selectedMonth - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If future date, return empty
    if (date > today) return null;

    // If Sunday, return empty (or holiday)
    if (date.getDay() === 0) return null;

    // If past/today and no status -> Alpha
    return 'absent';
  };

  const hasEffectiveCheckOut = (student, day) => {
    const attendance = student.attendance[day];
    if (attendance?.hasCheckOut) return true;

    const date = new Date(selectedYear, selectedMonth - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date > today) return null;
    if (date.getDay() === 0) return null;
    return false;
  };

  const calculateStats = (student) => {
    let h = 0, s = 0, i = 0, a = 0, t = 0, co = 0;
    
    daysArray.forEach(day => {
      const checkInStatus = getEffectiveCheckInStatus(student, day);
      const hasCheckOut = hasEffectiveCheckOut(student, day);
      if (checkInStatus === 'present') h++;
      else if (checkInStatus === 'sick') s++;
      else if (checkInStatus === 'permission') i++;
      else if (checkInStatus === 'absent') a++;
      else if (checkInStatus === 'late') t++;
      if (hasCheckOut) co++;
    });
    
    return { h, s, i, a, t, co };
  };

  const openExportDialog = () => {
    setExportParams({
      month: selectedMonth,
      year: selectedYear,
      grade: selectedGrade,
      classroom: selectedClassroom,
    });
    setExportDialogOpen(true);
  };

  const closeExportDialog = () => {
    if (!exportBusy) setExportDialogOpen(false);
  };

  const handleExportParamChange = (field, value) => {
    setExportParams((prev) => ({ ...prev, [field]: value }));
  };

  const fetchExportData = async (month, year, grade, classroom) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Belum login');
    const { data } = await axios.get(`${API_URL}/api/reports/monthly-recap`, {
      params: {
        month,
        year,
        grade: isTeacherTab ? 'all' : grade,
        classroom: isTeacherTab ? 'all' : classroom,
        person_type: personTab,
      },
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  };

  const handleExportPDF = async () => {
    try {
      setExportBusy(true);
      const { month, year, grade, classroom } = exportParams;
      const records = await fetchExportData(month, year, grade, classroom);
      if (!records || records.length === 0) {
        alert('Tidak ada data untuk periode/filter yang dipilih');
        return;
      }

      const lDays = new Date(year, month, 0).getDate();
      const lDaysArr = Array.from({ length: lDays }, (_, i) => i + 1);
      const isSundayLocal = (d) => new Date(year, month - 1, d).getDay() === 0;
      const monthName = months.find((m) => m.value === month)?.label ?? '';
      const recapLabel = isTeacherTab ? 'GURU' : 'SISWA';
      const gradeLabel = grade === 'all' ? 'Semua Kelas' : `Kelas ${grade}${classroom === 'all' ? '' : ' ' + classroom}`;
      const namaHeader = isTeacherTab ? 'Nama Guru' : 'Nama Siswa';
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const calculateStatsLocal = (student) => {
        let h = 0, s = 0, i = 0, a = 0, t = 0, co = 0;
        for (const d of lDaysArr) {
          const date = new Date(year, month - 1, d);
          const attendance = student.attendance[d];
          let checkInStatus = attendance?.checkInStatus ?? null;
          let hasCheckOut = attendance?.hasCheckOut ?? null;
          if (checkInStatus === null) {
            if (date > today) continue;
            if (date.getDay() === 0) continue;
            checkInStatus = 'absent';
          }
          if (hasCheckOut === null) {
            if (date > today) { hasCheckOut = null; }
            else if (date.getDay() === 0) { hasCheckOut = null; }
            else hasCheckOut = false;
          }
          if (checkInStatus === 'present') h++;
          else if (checkInStatus === 'sick') s++;
          else if (checkInStatus === 'permission') i++;
          else if (checkInStatus === 'absent') a++;
          else if (checkInStatus === 'late') t++;
          if (hasCheckOut === true) co++;
        }
        return { h, s, i, a, t, co };
      };

      const doc = new jsPDF('l', 'mm', 'a4');
      const PW = 297;
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`REKAP ABSENSI ${recapLabel}`, 148.5, 12, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Bulan: ${monthName} ${year}    ${isTeacherTab ? '' : gradeLabel}`, 148.5, 19, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      const subtitleRight = `Dicetak: ${new Date().toLocaleString('id-ID')}`;
      doc.text(subtitleRight, 293, 19, { align: 'right' });
      doc.setTextColor(0, 0, 0);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('Keterangan:', 4, 22);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);
      doc.text('M = MASUK     P = PULANG     T = TERLAMBAT', 4, 26);
      doc.setTextColor(0, 0, 0);

      const HEADER_DARK = [30, 41, 59];
      const HEADER_DATE = [51, 65, 85];
      const HEADER_MINGGU = [124, 58, 237];
      // Header Total H/S/I/A/P: warna SAMA PERSIS tabel UI (bg colored, white text)
      const HEADER_H_BG = [16, 185, 129];     // #10b981 hijau
      const HEADER_S_BG = [59, 130, 246];     // #3b82f6 biru
      const HEADER_I_BG = [245, 158, 11];     // #f59e0b amber
      const HEADER_A_BG = [239, 68, 68];      // #ef4444 merah
      const HEADER_P_BG = [6, 182, 212];      // #06b6d4 cyan
      const BODY_MINGGU_BG = [237, 233, 254];
      const BODY_MINGGU_TEXT = [109, 40, 217];

      const dateColsCount = lDaysArr.length;
      const totalCols = 2 + dateColsCount + 5;

      const headRow1 = [
        { content: 'No', rowSpan: 2, styles: { fillColor: HEADER_DARK, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } },
        { content: namaHeader, rowSpan: 2, styles: { fillColor: HEADER_DARK, textColor: 255, fontStyle: 'bold', halign: 'left', valign: 'middle', fontSize: 8 } },
        { content: `Tanggal (${monthName})`, colSpan: dateColsCount, styles: { fillColor: HEADER_DARK, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } },
        { content: 'Total', colSpan: 5, styles: { fillColor: HEADER_DARK, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } },
      ];
      const headRow2 = [];
      for (const d of lDaysArr) {
        if (isSundayLocal(d)) {
          headRow2.push({ content: `${d}\nMg`, styles: { fillColor: HEADER_MINGGU, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 7 } });
        } else {
          headRow2.push({ content: `${d}`, styles: { fillColor: HEADER_DATE, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } });
        }
      }
      headRow2.push({ content: 'H', styles: { fillColor: HEADER_H_BG, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } });
      headRow2.push({ content: 'S', styles: { fillColor: HEADER_S_BG, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } });
      headRow2.push({ content: 'I', styles: { fillColor: HEADER_I_BG, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } });
      headRow2.push({ content: 'A', styles: { fillColor: HEADER_A_BG, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } });
      headRow2.push({ content: 'P', styles: { fillColor: HEADER_P_BG, textColor: 255, fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 8 } });

      const bodyRows = records.map((student, idx) => {
        const stats = calculateStatsLocal(student);
        const row = [
          { content: idx + 1, styles: { halign: 'center', fontSize: 7 } },
          { content: student.name || '-', styles: { halign: 'left', fontSize: 7, cellPadding: 0.8 } },
        ];
        for (const d of lDaysArr) {
          if (isSundayLocal(d)) {
            row.push({ content: 'Mg\nMg', styles: { halign: 'center', fontSize: 7, fillColor: BODY_MINGGU_BG, textColor: BODY_MINGGU_TEXT, fontStyle: 'bold' } });
          } else {
            const att = student.attendance[d];
            let checkInStatus = att?.checkInStatus ?? null;
            let hasCheckOut = att?.hasCheckOut ?? null;
            const date = new Date(year, month - 1, d);
            if (checkInStatus === null) {
              if (date > today) { /* keep null */ }
              else if (date.getDay() === 0) { /* skip */ }
              else checkInStatus = 'absent';
            }
            if (hasCheckOut === null) {
              if (date > today) { hasCheckOut = null; }
              else if (date.getDay() === 0) { hasCheckOut = null; }
              else hasCheckOut = false;
            }
            const checkInCode = checkInStatus ? getStatusCode(checkInStatus) : (date > today ? '' : '-');
            const checkOutCode = hasCheckOut === true ? '✓' : (hasCheckOut === null ? '' : '-');
            let cellBg;
            let cellText = [0, 0, 0];
            let cellContent;
            if (date > today) {
              cellBg = undefined;
              cellContent = '';
            } else if (checkInStatus === 'present') {
              cellBg = [209, 250, 229]; cellText = [6, 95, 70];
              cellContent = `M ${checkInCode}\nP ${checkOutCode}`;
            } else if (checkInStatus === 'late') {
              cellBg = [254, 243, 199]; cellText = [146, 64, 14];
              cellContent = `M ${checkInCode}\nP ${checkOutCode}`;
            } else if (checkInStatus === 'sick') {
              cellBg = [224, 231, 255]; cellText = [55, 48, 163];
              cellContent = `M ${checkInCode}\nP ${checkOutCode}`;
            } else if (checkInStatus === 'permission') {
              cellBg = [219, 234, 254]; cellText = [30, 64, 175];
              cellContent = `M ${checkInCode}\nP ${checkOutCode}`;
            } else if (checkInStatus === 'absent') {
              cellBg = [254, 226, 226]; cellText = [185, 28, 28];
              cellContent = `M ${checkInCode}\nP ${checkOutCode}`;
            } else {
              cellBg = undefined;
              cellContent = `M -\nP ${checkOutCode}`;
            }
            row.push({ content: cellContent, styles: { halign: 'center', fontSize: 7, fillColor: cellBg, textColor: cellText, fontStyle: 'bold' } });
          }
        }
        row.push({ content: stats.h + stats.t, styles: { halign: 'center', fontSize: 8, fontStyle: 'bold' } });
        row.push({ content: stats.s, styles: { halign: 'center', fontSize: 8, fontStyle: 'bold' } });
        row.push({ content: stats.i, styles: { halign: 'center', fontSize: 8, fontStyle: 'bold' } });
        row.push({ content: stats.a, styles: { halign: 'center', fontSize: 8, fontStyle: 'bold', textColor: [185, 28, 28] } });
        row.push({ content: stats.co, styles: { halign: 'center', fontSize: 8, fontStyle: 'bold' } });
        return row;
      });

      // Total kolom Total H/S/I/A/P = 8 * 5 = 40mm, No=6, Nama=35, margin 3+3=6 → sisa untuk tanggal
      const availWidth = 297 - 6 - 6 - 35 - 8 * 5;
      const cellW = Math.max(3.5, Math.floor(availWidth / dateColsCount * 10) / 10);
      const columnStyles = { 0: { cellWidth: 6 }, 1: { cellWidth: 35 } };
      for (let i = 2; i < 2 + dateColsCount; i++) columnStyles[i] = { cellWidth: cellW };
      for (let i = 2 + dateColsCount; i < totalCols; i++) columnStyles[i] = { cellWidth: 8 };

      const result = autoTable(doc, {
        head: [headRow1, headRow2],
        body: bodyRows,
        startY: 30,
        styles: { cellPadding: 0.5, valign: 'middle', overflow: 'linebreak' },
        columnStyles,
        theme: 'grid',
        margin: { left: 3, right: 3, top: 30 },
        tableWidth: 'auto',
      });

      const finalY = (result?.finalY ?? 200) + 14;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const todayId = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`......................, ${todayId}`, 228, finalY);
      doc.text('Wali Kelas,', 228, finalY + 7);
      doc.text('( ______________________ )', 222, finalY + 32);
      const fileSuffix = grade === 'all' ? '' : (classroom === 'all' ? `_kelas_${grade}` : `_kelas_${grade}${classroom}`);
      doc.save(`rekap_absensi_${recapLabel.toLowerCase()}_${month}_${year}${fileSuffix}.pdf`);
    } catch (err) {
      console.error(err);
      alert('Gagal export PDF: ' + (err.message || err));
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
            Rekap Bulanan {isTeacherTab ? 'Guru' : 'Siswa'}
          </Typography>
          <ToggleButtonGroup
            value={personTab}
            exclusive
            onChange={(_, v) => v && setPersonTab(v)}
            size="small"
            sx={{ mt: 1 }}
          >
            <ToggleButton value="student">Siswa</ToggleButton>
            <ToggleButton value="teacher">Guru</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={() => window.print()}
          >
            Cetak
          </Button>
          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />} 
            onClick={openExportDialog}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Bulan</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Bulan"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {months.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tahun</InputLabel>
                <Select
                  value={selectedYear}
                  label="Tahun"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!isTeacherTab && (
              <>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Kelas</InputLabel>
                    <Select
                      value={selectedGrade}
                      label="Kelas"
                      onChange={(e) => setSelectedGrade(e.target.value)}
                    >
                      <MenuItem value="all">Semua</MenuItem>
                      {grades.map((g) => (
                        <MenuItem key={g} value={g}>Kelas {g}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rombel</InputLabel>
                    <Select
                      value={selectedClassroom}
                      label="Rombel"
                      onChange={(e) => setSelectedClassroom(e.target.value)}
                    >
                      <MenuItem value="all">Semua</MenuItem>
                      {classrooms.map((c) => (
                        <MenuItem key={c} value={c}>Rombel {c}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12} md={2}>
              <Button 
                variant="contained" 
                fullWidth 
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Tampilkan'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Report View */}
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            REKAP ABSENSI {isTeacherTab ? 'GURU' : 'SISWA'}
          </Typography>
          <Typography variant="subtitle1">
            Bulan: {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </Typography>
          {!isTeacherTab && (selectedGrade !== 'all' || selectedClassroom !== 'all') && (
            <Typography variant="subtitle2">
              Filter: {selectedGrade === 'all' ? 'Semua Kelas' : `Kelas ${selectedGrade}`}
              {selectedClassroom === 'all' ? '' : ` • Rombel ${selectedClassroom}`}
            </Typography>
          )}
        </Box>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 1200, border: '1px solid #e2e8f0' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1e293b' }}>
                <TableCell rowSpan={2} sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', width: 40 }}>No</TableCell>
                <TableCell rowSpan={2} sx={{ color: 'white', border: '1px solid #475569', minWidth: 200 }}>
                  {isTeacherTab ? 'Nama Guru' : 'Nama Siswa'}
                </TableCell>
                <TableCell colSpan={daysInMonth * 2} sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center' }}>
                  Tanggal ({months.find(m => m.value === selectedMonth)?.label})
                </TableCell>
                <TableCell colSpan={5} sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center' }}>Total</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#334155' }}>
                {daysArray.map(day => (
                  <React.Fragment key={day}>
                    <TableCell
                      sx={{
                        color: 'white',
                        border: '1px solid #475569',
                        textAlign: 'center',
                        minWidth: 28,
                        padding: '2px 4px',
                        lineHeight: 1.2,
                        ...(isSunday(day) ? sundayHeaderSx : {}),
                      }}
                    >
                      {isSunday(day) ? (
                        <Tooltip title="Hari Minggu (libur)" arrow>
                          <Box component="span" sx={{ display: 'block', cursor: 'help' }}>
                            <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>{day}</Box>
                            <Box component="span" sx={{ display: 'block', fontSize: '0.6rem', letterSpacing: 0.5 }}>Mg</Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box>
                          <Box sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{day}</Box>
                          <Box sx={{ fontSize: '0.6rem' }}>Masuk</Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        border: '1px solid #475569',
                        textAlign: 'center',
                        minWidth: 28,
                        padding: '2px 4px',
                        lineHeight: 1.2,
                        ...(isSunday(day) ? sundayHeaderSx : {}),
                      }}
                    >
                      {isSunday(day) ? (
                        <Box sx={{ fontSize: '0.6rem' }}>Mg</Box>
                      ) : (
                        <Box sx={{ fontSize: '0.6rem' }}>Pulang</Box>
                      )}
                    </TableCell>
                  </React.Fragment>
                ))}
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#10b981', width: 30 }}>H</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#3b82f6', width: 30 }}>S</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#f59e0b', width: 30 }}>I</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#ef4444', width: 30 }}>A</TableCell>
                <TableCell sx={{ color: 'white', border: '1px solid #475569', textAlign: 'center', bgcolor: '#06b6d4', width: 30 }}>P</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={daysInMonth * 2 + 7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={daysInMonth * 2 + 7} align="center" sx={{ py: 4 }}>
                    Tidak ada data {isTeacherTab ? 'guru' : 'siswa'}
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student, index) => {
                  const stats = calculateStats(student);
                  return (
                    <TableRow key={student.id} hover>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0' }}>{index + 1}</TableCell>
                      <TableCell sx={{ border: '1px solid #e2e8f0', fontWeight: 500 }}>{student.name}</TableCell>
                      {daysArray.map(day => {
                        const sunday = isSunday(day);
                        const checkInStatus = getEffectiveCheckInStatus(student, day);
                        const hasCheckOut = hasEffectiveCheckOut(student, day);
                        const checkInCode = sunday ? 'Mg' : (checkInStatus ? getStatusCode(checkInStatus) : '-');
                        const checkInColor = sunday ? '#ede9fe' : (checkInStatus ? getStatusColor(checkInStatus) : 'transparent');
                        const checkOutCode = sunday ? 'Mg' : (hasCheckOut ? '✓' : (hasCheckOut === null ? '' : '-'));
                        const checkOutColor = sunday ? '#ede9fe' : (hasCheckOut ? '#d1fae5' : 'transparent');
                        return (
                          <React.Fragment key={day}>
                            <TableCell
                              align="center"
                              sx={{
                                border: '1px solid #e2e8f0',
                                bgcolor: checkInColor,
                                padding: '2px',
                                fontSize: '0.75rem',
                                ...(sunday ? sundayBodySx : {}),
                              }}
                            >
                              {sunday || checkInCode !== '-' ? checkInCode : ''}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                border: '1px solid #e2e8f0',
                                bgcolor: checkOutColor,
                                padding: '2px',
                                fontSize: '0.75rem',
                                ...(sunday ? sundayBodySx : {}),
                              }}
                            >
                              {sunday || checkOutCode !== '-' ? checkOutCode : ''}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.h + stats.t}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.s}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.i}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold', color: 'error.main' }}>{stats.a}</TableCell>
                      <TableCell align="center" sx={{ border: '1px solid #e2e8f0', fontWeight: 'bold' }}>{stats.co}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            fontSize: '0.8rem',
            color: '#475569',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 28, height: 20, bgcolor: '#7c3aed', borderRadius: 0.5, border: '1px solid #5b21b6' }} />
            <Typography variant="caption">Kolom ungu = tanggal hari Minggu</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: '#ede9fe',
                color: '#5b21b6',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: 0.5,
                border: '1px solid #c4b5fd',
              }}
            >
              Mg
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: '#dbeafe',
                color: '#1d4ed8',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: 0.5,
                border: '1px solid #93c5fd',
              }}
            >
              I
            </Box>
            <Typography variant="caption">= Izin</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: '#e0e7ff',
                color: '#4338ca',
                fontWeight: 700,
                fontSize: '0.7rem',
                borderRadius: 0.5,
                border: '1px solid #a5b4fc',
              }}
            >
              S
            </Box>
            <Typography variant="caption">= Sakit</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ textAlign: 'center', minWidth: 200 }}>
            <Typography variant="body2">
              .................., {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>Wali Kelas,</Typography>
            <Box sx={{ height: 60 }} />
            <Typography variant="body2" sx={{ borderTop: '1px solid black', pt: 1 }}>
              (_______________________)
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Dialog open={exportDialogOpen} onClose={closeExportDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Export PDF Rekap Bulanan
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Bulan</InputLabel>
            <Select
              label="Bulan"
              value={exportParams.month}
              onChange={(e) => handleExportParamChange('month', e.target.value)}
              disabled={exportBusy}
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Tahun</InputLabel>
            <Select
              label="Tahun"
              value={exportParams.year}
              onChange={(e) => handleExportParamChange('year', e.target.value)}
              disabled={exportBusy}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {!isTeacherTab && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Tingkat Kelas</InputLabel>
                <Select
                  label="Tingkat Kelas"
                  value={exportParams.grade}
                  onChange={(e) => handleExportParamChange('grade', e.target.value)}
                  disabled={exportBusy}
                >
                  <MenuItem value="all">Semua Kelas</MenuItem>
                  <MenuItem value="1">Kelas 1</MenuItem>
                  <MenuItem value="2">Kelas 2</MenuItem>
                  <MenuItem value="3">Kelas 3</MenuItem>
                  <MenuItem value="4">Kelas 4</MenuItem>
                  <MenuItem value="5">Kelas 5</MenuItem>
                  <MenuItem value="6">Kelas 6</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Rombel</InputLabel>
                <Select
                  label="Rombel"
                  value={exportParams.classroom}
                  onChange={(e) => handleExportParamChange('classroom', e.target.value)}
                  disabled={exportBusy}
                >
                  <MenuItem value="all">Semua Rombel</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                  <MenuItem value="B">B</MenuItem>
                  <MenuItem value="C">C</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {isTeacherTab && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              Mode Guru: semua guru akan diexport sesuai bulan &amp; tahun yang dipilih.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeExportDialog} disabled={exportBusy} sx={{ color: 'text.secondary' }}>
            Batal
          </Button>
          <Button
            variant="contained"
            onClick={handleExportPDF}
            disabled={exportBusy}
            startIcon={exportBusy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <DownloadIcon />}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              px: 3,
            }}
          >
            {exportBusy ? 'Membuat PDF...' : 'Export PDF'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MonthlyRecap;
